import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { AttachmentType } from '@/entities/consignments';
import type { ReceivePendingAttachment } from '@/entities/receive';
import { generateTempId, validateFile } from '@/shared/lib';

import { CONFIRM_ARRIVAL_DEFAULT_ATTACHMENT_TYPE, CONFIRM_ARRIVAL_MAX_FILES } from './constants';

/**
 * Files picked in the modal but not sent yet.
 *
 * They ride along with the receipt itself — there is no upload endpoint to call
 * first — so they live here until submit, exactly like the quantities. Each file
 * carries its own type, which is what the backend's per-file `attachmentType`
 * alignment expects; the comment is one for the whole upload, because that is
 * all the API stores.
 */
export const useConfirmArrivalAttachments = () => {
  const { t } = useTranslation();
  const [pending, setPending] = useState<ReceivePendingAttachment[]>([]);
  const [comment, setComment] = useState('');

  /**
   * Invalid files are dropped with a toast from `validateFile`; valid ones are
   * kept up to `CONFIRM_ARRIVAL_MAX_FILES`, and the overflow is refused with
   * legacy's message.
   */
  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const valid = Array.from(files).filter((file) => validateFile(file).isValid);
      const room = Math.max(CONFIRM_ARRIVAL_MAX_FILES - pending.length, 0);

      if (valid.length > room) {
        toast.error(t('gen.maxfiles.message', 'You have reached the maximum number of files you can attach'));
      }

      const accepted = valid.slice(0, room).map((file) => ({
        tempId: generateTempId(),
        file,
        attachmentType: CONFIRM_ARRIVAL_DEFAULT_ATTACHMENT_TYPE
      }));

      if (accepted.length > 0) setPending((prev) => [...prev, ...accepted]);
    },
    [pending.length, t]
  );

  const setAttachmentType = useCallback((tempId: string, attachmentType: AttachmentType) => {
    setPending((prev) => prev.map((item) => (item.tempId === tempId ? { ...item, attachmentType } : item)));
  }, []);

  const removeFile = useCallback((tempId: string) => {
    setPending((prev) => prev.filter((item) => item.tempId !== tempId));
  }, []);

  const reset = useCallback(() => {
    setPending([]);
    setComment('');
  }, []);

  return { pending, comment, setComment, addFiles, setAttachmentType, removeFile, reset };
};

export type ConfirmArrivalAttachments = ReturnType<typeof useConfirmArrivalAttachments>;
